import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { FIELD_KEYS, FIELD_LABELS } from "../lib/api";
import { X, FloppyDisk, FileText } from "@phosphor-icons/react";

export default function FileMappingDialog({ file, onClose, onSaved }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mapping, setMapping] = useState({});
  const [hasHeader, setHasHeader] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/files/${file.id}/preview`);
      setPreview(data);
      setHasHeader(!!data.has_header);
      const initial = {};
      for (const k of FIELD_KEYS) {
        const v = data?.mapping?.[k];
        initial[k] = v === undefined || v === null ? "" : String(v);
      }
      setMapping(initial);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Не удалось загрузить файл с Drive");
      onClose();
    } finally {
      setLoading(false);
    }
  }, [file.id, onClose]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        has_header: hasHeader,
        mapping: Object.fromEntries(
          FIELD_KEYS.map((k) => [k, mapping[k] === "" ? null : parseInt(mapping[k], 10)])
        ),
      };
      await api.put(`/files/${file.id}/mapping`, payload);
      toast.success("Маппинг сохранён");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ошибка сохранения маппинга");
    } finally {
      setSaving(false);
    }
  };

  const cols = preview?.total_cols || 0;
  const colOptions = Array.from({ length: cols }, (_, i) => i);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
      data-testid="mapping-dialog-overlay"
    >
      <div
        className="bg-white border border-[#18181b] w-full max-w-4xl max-h-[95vh] flex flex-col hard-shadow"
        onClick={(e) => e.stopPropagation()}
        data-testid="mapping-dialog"
      >
        {/* Header */}
        <div className="border-b border-[#d4d4d8] px-6 py-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="mono-label mb-1">FILE MAPPING</div>
            <div className="flex items-center gap-2">
              <FileText size={18} weight="regular" className="text-[#52525b] shrink-0" />
              <h2 className="text-lg font-semibold tracking-tight truncate">
                {file.file_name}
              </h2>
            </div>
            <div className="text-xs text-[#52525b] mt-1 font-mono">
              {file.folder_name} / {file.fmt?.toUpperCase()}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 border border-[#d4d4d8] hover:bg-[#18181b] hover:text-white hover:border-[#18181b] transition-colors duration-150"
            data-testid="mapping-dialog-close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1">
          {loading && <div className="p-8 mono-label">ЗАГРУЗКА ПРЕДПРОСМОТРА...</div>}

          {!loading && preview && (
            <div className="p-6 space-y-6">
              {/* Preview table */}
              <section>
                <div className="flex items-center justify-between mb-2">
                  <div className="mono-label">ПРЕДПРОСМОТР (первые строки с Drive)</div>
                  <div className="text-xs text-[#52525b] font-mono">
                    {preview.total_cols} столбцов
                    {preview.csv_delimiter && (
                      <> · delim=<b>{preview.csv_delimiter === "\t" ? "TAB" : preview.csv_delimiter}</b></>
                    )}
                    {preview.csv_encoding && <> · enc={preview.csv_encoding}</>}
                  </div>
                </div>

                <div className="border border-[#d4d4d8] overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-[#f5f5f4] border-b border-[#d4d4d8]">
                        <th className="px-2 py-1.5 mono-label text-left sticky left-0 bg-[#f5f5f4]">
                          #
                        </th>
                        {colOptions.map((i) => (
                          <th
                            key={i}
                            className="px-3 py-1.5 mono-label text-left whitespace-nowrap"
                          >
                            COL {i}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((row, ri) => (
                        <tr
                          key={ri}
                          className={`border-b border-[#d4d4d8] last:border-b-0 ${
                            hasHeader && ri === 0 ? "bg-[#fff8e1]" : ""
                          }`}
                        >
                          <td className="px-2 py-1.5 mono-label sticky left-0 bg-white font-mono">
                            {ri}
                          </td>
                          {colOptions.map((ci) => (
                            <td
                              key={ci}
                              className="px-3 py-1.5 font-mono whitespace-nowrap max-w-[240px] overflow-hidden text-ellipsis"
                            >
                              {row[ci] !== undefined && row[ci] !== null ? String(row[ci]) : ""}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <label className="inline-flex items-center gap-2 mt-3 text-sm cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={hasHeader}
                    onChange={(e) => setHasHeader(e.target.checked)}
                    data-testid="mapping-has-header"
                    className="accent-[#0047FF]"
                  />
                  Первая строка — заголовок (не трогать при добавлении)
                </label>
              </section>

              {/* Mapping */}
              <section>
                <div className="mono-label mb-3">МАППИНГ ПОЛЕЙ</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {FIELD_KEYS.map((k) => (
                    <div key={k} className="space-y-1.5">
                      <label
                        className="mono-label block"
                        htmlFor={`map-${k}`}
                      >
                        {FIELD_LABELS[k]}
                      </label>
                      <select
                        id={`map-${k}`}
                        data-testid={`column-mapper-${k}`}
                        value={mapping[k] ?? ""}
                        onChange={(e) =>
                          setMapping((m) => ({ ...m, [k]: e.target.value }))
                        }
                        className="w-full font-mono text-sm border border-[#d4d4d8] px-2.5 py-2 bg-white focus:outline-none focus:border-[#0047FF] focus:ring-1 focus:ring-[#0047FF]"
                      >
                        <option value="">— нет в этом файле —</option>
                        {colOptions.map((i) => (
                          <option key={i} value={i}>
                            COL {i}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[#52525b] mt-3">
                  Выберите, в какой столбец попадёт каждое поле формы. Если поля нет
                  в этом файле — оставьте «нет в этом файле».
                </p>
              </section>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#d4d4d8] px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="border border-[#18181b] bg-transparent text-[#18181b] px-4 py-2 text-sm hover:bg-[#18181b] hover:text-white transition-colors duration-150"
            data-testid="mapping-cancel"
          >
            Отмена
          </button>
          <button
            onClick={save}
            disabled={saving || loading}
            data-testid="mapping-save"
            className="inline-flex items-center gap-2 bg-[#0047FF] text-white px-4 py-2 text-sm hover:bg-[#0036CC] transition-colors duration-150 disabled:opacity-50"
          >
            <FloppyDisk size={14} weight="bold" />
            {saving ? "Сохранение..." : "Сохранить маппинг"}
          </button>
        </div>
      </div>
    </div>
  );
}
